import React, { useEffect, useState } from "react";
import { Smartphone, Save, BellRing, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { PageHeader, LoadingBlock } from "../../components/app/ui";
import { adminApi, errorMessage } from "../../lib/api";

function ToggleRow({ icon: Icon, title, text, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-4 rounded-2xl border border-surface-200 bg-surface-50 p-4" data-testid={testId}>
      <Icon className="h-5 w-5 shrink-0 text-brand" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-xs text-ink-soft">{text}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function AutoCard({ platform, latest, liveMax }) {
  return (
    <div className="rounded-2xl border border-surface-200 bg-white p-4" data-testid={`auto-${platform}`}>
      <p className="text-xs font-medium text-ink-soft">{platform === "android" ? "Android" : "iOS"} latest <span className="font-semibold text-green-600">(auto-detected)</span></p>
      <p className="mt-1 text-2xl font-bold text-ink">{latest}</p>
      <p className="mt-1 text-xs text-ink-muted">Live max seen: {liveMax ?? 0}</p>
    </div>
  );
}

export default function AppVersion() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    prompt_update: true, auto_detect: true,
    android_min_build: "", ios_min_build: "",
    android_latest_manual: "", ios_latest_manual: "",
    android_store_url: "", ios_store_url: "",
    update_title: "", update_message: "", forced_message: "", whats_new: "",
  });

  const load = () => adminApi.versionConfig().then((c) => {
    setCfg(c);
    setForm({
      prompt_update: c.prompt_update !== false,
      auto_detect: c.auto_detect !== false,
      android_min_build: String(c.android_min_build ?? ""),
      ios_min_build: String(c.ios_min_build ?? ""),
      android_latest_manual: c.android_latest_manual ? String(c.android_latest_manual) : "",
      ios_latest_manual: c.ios_latest_manual ? String(c.ios_latest_manual) : "",
      android_store_url: c.android_store_url || "",
      ios_store_url: c.ios_store_url || "",
      update_title: c.update_title || "",
      update_message: c.update_message || "",
      forced_message: c.forced_message || "",
      whats_new: c.whats_new || "",
    });
  }).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setToggle = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const c = await adminApi.setVersionConfig({
        ...form,
        android_min_build: parseInt(form.android_min_build, 10) || 0,
        ios_min_build: parseInt(form.ios_min_build, 10) || 0,
        android_latest_manual: parseInt(form.android_latest_manual, 10) || 0,
        ios_latest_manual: parseInt(form.ios_latest_manual, 10) || 0,
      });
      setCfg(c);
      toast.success("Update settings saved");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="App update & version gate" actions={cfg && <Button variant="outline" onClick={load} className="rounded-full" data-testid="app-version-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>} />
      {!cfg ? <LoadingBlock rows={3} /> : (
        <div className="kt-card max-w-3xl p-6">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Smartphone className="h-5 w-5" /></span>
            <div>
              <h2 className="text-lg font-semibold text-ink">Mobile update gate</h2>
              <p className="text-sm text-ink-soft">Prompt users on older app versions to update. Changes apply on next app launch — no new build needed.</p>
            </div>
          </div>

          <div className="space-y-3">
            <ToggleRow icon={BellRing} title="Prompt users to update" text="Shows an optional 'Update available' modal to anyone below the latest version." checked={form.prompt_update} onChange={setToggle("prompt_update")} testId="toggle-prompt" />
            <ToggleRow icon={RefreshCw} title="Auto-detect latest version" text="Learns the newest build code from live apps — no need to type it. Turn off to set it manually." checked={form.auto_detect} onChange={setToggle("auto_detect")} testId="toggle-autodetect" />
          </div>

          {form.auto_detect ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <AutoCard platform="android" latest={cfg.android_latest_auto} liveMax={cfg.live_max_android} />
              <AutoCard platform="ios" latest={cfg.ios_latest_auto} liveMax={cfg.live_max_ios} />
            </div>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Android latest versionCode (manual)</Label>
                <Input value={form.android_latest_manual} onChange={set("android_latest_manual")} inputMode="numeric" placeholder="275" data-testid="android-latest-manual" />
                <p className="mt-1 text-xs text-ink-muted">Update gate currently uses: {cfg.android_latest_auto}</p>
              </div>
              <div>
                <Label>iOS latest buildNumber (manual)</Label>
                <Input value={form.ios_latest_manual} onChange={set("ios_latest_manual")} inputMode="numeric" placeholder="12" data-testid="ios-latest-manual" />
                <p className="mt-1 text-xs text-ink-muted">Update gate currently uses: {cfg.ios_latest_auto}</p>
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div><Label>Android force-below versionCode</Label><Input value={form.android_min_build} onChange={set("android_min_build")} inputMode="numeric" placeholder="120" data-testid="android-min-build" /></div>
            <div><Label>iOS force-below buildNumber</Label><Input value={form.ios_min_build} onChange={set("ios_min_build")} inputMode="numeric" placeholder="1" data-testid="ios-min-build" /></div>
          </div>

          <div className="mt-4 space-y-4">
            <div><Label>Android store URL</Label><Input value={form.android_store_url} onChange={set("android_store_url")} placeholder="https://play.google.com/store/apps/details?id=..." data-testid="android-store-url" /></div>
            <div><Label>iOS store URL</Label><Input value={form.ios_store_url} onChange={set("ios_store_url")} placeholder="https://apps.apple.com/app/id..." data-testid="ios-store-url" /></div>
            <div><Label>Modal title</Label><Input value={form.update_title} onChange={set("update_title")} placeholder="Update available" data-testid="modal-title" /></div>
            <div><Label>Optional update message</Label><Textarea value={form.update_message} onChange={set("update_message")} rows={2} placeholder="A new version of KohimaTaxi is available with improvements and fixes." data-testid="optional-message" /></div>
            <div><Label>Forced update message</Label><Textarea value={form.forced_message} onChange={set("forced_message")} rows={2} placeholder="Please update to the latest version to keep using KohimaTaxi." data-testid="forced-message" /></div>
            <div><Label>What's new (one point per line)</Label><Textarea value={form.whats_new} onChange={set("whats_new")} rows={4} placeholder={"Faster search\nNew location autocomplete\nBug fixes"} data-testid="whats-new" /></div>
          </div>

          <Button onClick={save} disabled={saving} className="mt-6 w-full rounded-full bg-ink text-white hover:bg-ink/90" data-testid="app-version-save"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save update settings"}</Button>
        </div>
      )}
    </>
  );
}
